import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import emailjs from '@emailjs/browser';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Mail, User, Building, MapPin, Target } from "lucide-react";

// Free email providers to block
const freeEmailProviders = [
  'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'live.com', 
  'icloud.com', 'me.com', 'protonmail.com', 'proton.me', 'aol.com',
  'msn.com', 'ymail.com', 'rocketmail.com', 'mail.com', 'gmx.com',
  'web.de', 'mail.ru', 'yandex.com', 'qq.com', '163.com', '126.com'
];

const contactSchema = z.object({
  name: z.string().trim().min(1, { message: "Name is required" }).max(100, { message: "Name must be less than 100 characters" }),
  email: z.string()
    .trim()
    .email({ message: "Invalid email address" })
    .max(255, { message: "Email must be less than 255 characters" })
    .refine((email) => {
      const domain = email.split('@')[1]?.toLowerCase();
      return domain && !freeEmailProviders.includes(domain);
    }, { message: "Please use your institutional email (no Gmail/Hotmail/Outlook, etc.)" }),
  company: z.string().trim().min(1, { message: "Company/Organization is required" }).max(100, { message: "Company name must be less than 100 characters" }),
  location: z.string().trim().max(100, { message: "Location must be less than 100 characters" }).optional(),
  areaOfFocus: z.string().trim().max(200, { message: "Area of focus must be less than 200 characters" }).optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface ContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContactModal({ open, onOpenChange }: ContactModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      location: "",
      areaOfFocus: "",
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    
    try {
      // EmailJS configuration - you'll need to set these up in your EmailJS account
      const serviceId = 'YOUR_SERVICE_ID';
      const templateId = 'YOUR_TEMPLATE_ID';
      const publicKey = 'YOUR_PUBLIC_KEY';
      
      // Template parameters for EmailJS
      const templateParams = {
        to_email: 'info@vidavo.eu',
        from_name: data.name,
        from_email: data.email,
        company: data.company,
        location: data.location || 'Not specified',
        area_of_focus: data.areaOfFocus || 'Not specified',
        message: `New Partnership Inquiry

Name: ${data.name}
Email: ${data.email}
Company/Organization: ${data.company}
${data.location ? `Location: ${data.location}` : ''}
${data.areaOfFocus ? `Area of Focus: ${data.areaOfFocus}` : ''}

Please contact this person to discuss partnership opportunities.`
      };

      // Send email using EmailJS
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      toast({
        title: "Partnership request sent!",
        description: "Your partnership inquiry has been sent successfully. We'll get back to you soon.",
      });
      
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('EmailJS Error:', error);
      toast({
        title: "Error sending email",
        description: "Failed to send partnership request. Please contact temmanouilidi@vidavo.eu directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[92vw] max-w-[480px] max-h-[92dvh] sm:max-h-[85vh] overflow-y-auto overscroll-contain mx-auto rounded-lg p-4 sm:p-6 pb-[env(safe-area-inset-bottom)]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-primary">Join the Vidavo Network</DialogTitle>
          <p className="text-muted-foreground mt-2">
            We welcome partners and authorized resellers who share our vision for advancing digital health. 
            Please fill in the form below and our team will contact you.
          </p>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Full Name *
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your full name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Address *
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your email address" type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Company/Organization *
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your company/organization name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Location
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your location (optional)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="areaOfFocus"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Area of Focus
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Telemedicine, Digital Health, AI in Healthcare (optional)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? "Sending..." : "Send Partnership Request"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}